import DOMPurify from 'dompurify';

import { opTime } from 'utils/dates';

import { heliumReceive } from '../../app/utils/helpers';

// Legacy badge data (original format for local HTML rendering)
export interface BadgeData {
  visitorType: string;
  visitorName: string;
  visitorCompany: string;
  hostLabel: string;
  hostName: string;
  orgName: string;
  visitDate: string;
}

// New badge data (for backend PNG generation)
export interface BadgeImageRequest {
  orgId: number;
  visitId: number;
  visitorId: number;
  visitorTypeLabel?: string;
  hostLabel?: string;
}

export interface BadgePngResponse {
  badgeImage: string; // Base64 data URI
  format: 'png';
  dimensions: {
    width: number;
    height: number;
    dpi: number;
  };
}

/**
 * Generate visitor badge PNG from backend
 * @param request - Badge request containing orgId, visitId, visitorId, and optional translated labels
 * @returns Promise<BadgePngResponse> - PNG image as Base64 data URI with metadata
 */
export async function generateBadgePng(
  request: BadgeImageRequest,
): Promise<BadgePngResponse> {
  const { orgId, visitId, visitorId, visitorTypeLabel, hostLabel } = request;

  // Build query params with translated labels
  const queryParams: Record<string, string> = {};
  if (visitorTypeLabel) {
    queryParams.visitorTypeLabel = visitorTypeLabel;
  }
  if (hostLabel) {
    queryParams.hostLabel = hostLabel;
  }

  // Call backend API to generate badge PNG
  // heliumReceive signature: (endpointName, requisites, options) for GET requests
  const response: any = await heliumReceive(
    'generateVisitorBadgeImage',
    [orgId, visitId, visitorId], // path parameters
    { queryStringParams: queryParams }, // query parameters
  );

  if (response?.errorMessage) {
    throw new Error(response.errorMessage);
  }

  // Response format: { data: { badgeImage, format, dimensions }, meta: {...} }
  const responseData = response?.data;

  if (
    !responseData?.badgeImage ||
    typeof responseData.badgeImage !== 'string' ||
    !responseData.badgeImage.startsWith('data:image/png;base64,')
  ) {
    throw new Error('Invalid response format from badge PNG API');
  }

  return {
    badgeImage: responseData.badgeImage,
    format: responseData.format || 'png',
    dimensions: responseData.dimensions || {
      width: 1181, // 100mm at 300 DPI
      height: 732, // 62mm at 300 DPI
      dpi: 300,
    },
  };
}

// Badge label is 3.94in x 2.44in (100mm x 62mm die-cut).
const BADGE_WIDTH_IN = 3.94;
const BADGE_HEIGHT_IN = 2.44;

/**
 * Image processing applied before printing, mirroring the iOS kiosk pipeline
 * (upscale + CIColorControls + CISharpenLuminance). The browser badge print
 * path otherwise sends the raw backend PNG untouched, which prints brighter and
 * softer than the kiosk.
 *
 * NOTE: replace these defaults with the kiosk's actual `ImageProcessingConstants`
 * values to match it exactly.
 */
export interface BadgePrintOptions {
  /** Kiosk: ImageProcessingConstants.upscaleFactor (high-quality upscale). */
  upscaleFactor?: number;
  /** CIColorControls kCIInputBrightnessKey (-1..1, additive). Negative = darker. */
  brightness?: number;
  /** CIColorControls kCIInputContrastKey (1 = unchanged, >1 = more contrast). */
  contrast?: number;
  /** CIColorControls kCIInputSaturationKey (1 = unchanged, 0 = grayscale). */
  saturation?: number;
  /** CISharpenLuminance sharpness (0 = none, ~0.4 = Core Image default). */
  sharpness?: number;
  /** Skip all processing and print the raw PNG (debug / comparison). */
  raw?: boolean;
}

// Mirrors the iOS kiosk's ImageProcessingConstants so the printed photo matches.
const DEFAULT_UPSCALE_FACTOR = 3;
const DEFAULT_BRIGHTNESS = -0.2;
const DEFAULT_CONTRAST = 1.2;
const DEFAULT_SATURATION = 1;
const DEFAULT_SHARPNESS = 0.8;

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load badge image'));
    img.src = src;
  });
}

// Pixels at/above this luma (0-255) are treated as badge background / light
// whitespace and left untouched, so darkening the photo does not dirty the
// white badge (the backend PNG composites photo + text + white background into
// one image, and additive brightness/contrast would otherwise gray out the
// background).
const WHITE_PRESERVE_LUMA = 245;

/**
 * CIColorControls equivalent: saturation, then contrast (around mid-gray), then
 * brightness (additive), applied in place on normalized RGB.
 *
 * Near-white pixels are preserved so only the photo/content is toned, not the
 * badge's white background.
 */
function applyColorControls(
  imageData: ImageData,
  brightness: number,
  contrast: number,
  saturation: number,
): void {
  const { data } = imageData;
  for (let i = 0; i < data.length; i += 4) {
    // Preserve the white background / light whitespace; only tone the content.
    const luma255 = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    if (luma255 < WHITE_PRESERVE_LUMA) {
      let r = data[i] / 255;
      let g = data[i + 1] / 255;
      let b = data[i + 2] / 255;

      // Saturation (mix toward Rec. 601 luma).
      const luma = 0.299 * r + 0.587 * g + 0.114 * b;
      r = luma + saturation * (r - luma);
      g = luma + saturation * (g - luma);
      b = luma + saturation * (b - luma);

      // Contrast around 0.5, then additive brightness.
      r = (r - 0.5) * contrast + 0.5 + brightness;
      g = (g - 0.5) * contrast + 0.5 + brightness;
      b = (b - 0.5) * contrast + 0.5 + brightness;

      data[i] = Math.min(255, Math.max(0, r * 255));
      data[i + 1] = Math.min(255, Math.max(0, g * 255));
      data[i + 2] = Math.min(255, Math.max(0, b * 255));
    }
  }
}

/**
 * Separable box blur (running sum, O(n) regardless of radius) with edge
 * clamping. Used as the low-pass for the unsharp mask.
 */
function boxBlur(
  src: Float32Array,
  width: number,
  height: number,
  radius: number,
): Float32Array {
  const window = radius * 2 + 1;
  const horizontal = new Float32Array(src.length);

  for (let y = 0; y < height; y += 1) {
    const base = y * width;
    let sum = 0;
    for (let k = -radius; k <= radius; k += 1) {
      sum += src[base + Math.min(Math.max(k, 0), width - 1)];
    }
    for (let x = 0; x < width; x += 1) {
      horizontal[base + x] = sum / window;
      const outIdx = Math.min(Math.max(x - radius, 0), width - 1);
      const inIdx = Math.min(Math.max(x + radius + 1, 0), width - 1);
      sum += src[base + inIdx] - src[base + outIdx];
    }
  }

  const blurred = new Float32Array(src.length);
  for (let x = 0; x < width; x += 1) {
    let sum = 0;
    for (let k = -radius; k <= radius; k += 1) {
      sum += horizontal[Math.min(Math.max(k, 0), height - 1) * width + x];
    }
    for (let y = 0; y < height; y += 1) {
      blurred[y * width + x] = sum / window;
      const outY = Math.min(Math.max(y - radius, 0), height - 1);
      const inY = Math.min(Math.max(y + radius + 1, 0), height - 1);
      sum += horizontal[inY * width + x] - horizontal[outY * width + x];
    }
  }

  return blurred;
}

/**
 * CISharpenLuminance equivalent: unsharp mask on the luminance channel. The
 * blur radius scales with the image so it enhances mid-scale edges (facial
 * contours) rather than only single-pixel detail; the recovered high-frequency
 * component is added back to RGB equally so edges crisp up without color
 * fringing.
 */
function applySharpenLuminance(
  imageData: ImageData,
  width: number,
  height: number,
  sharpness: number,
  radius: number,
): void {
  if (sharpness <= 0) return;

  const { data } = imageData;
  const count = width * height;
  const luma = new Float32Array(count);
  for (let i = 0; i < count; i += 1) {
    luma[i] =
      0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  }

  const blurred = boxBlur(luma, width, height, Math.max(1, radius));

  // Amount scales the Core Image sharpness into a visible unsharp strength.
  const amount = sharpness * 2;

  for (let i = 0; i < count; i += 1) {
    const delta = amount * (luma[i] - blurred[i]);
    const p = i * 4;
    data[p] = Math.min(255, Math.max(0, data[p] + delta));
    data[p + 1] = Math.min(255, Math.max(0, data[p + 1] + delta));
    data[p + 2] = Math.min(255, Math.max(0, data[p + 2] + delta));
  }
}

/**
 * Replicate the kiosk's pre-print processing in the browser: upscale with
 * high-quality interpolation, apply color controls, and sharpen luminance.
 * Returns a PNG data URI (or the original source on failure).
 */
function processBadgeImage(
  source: HTMLImageElement,
  {
    upscaleFactor,
    brightness,
    contrast,
    saturation,
    sharpness,
  }: Required<Omit<BadgePrintOptions, 'raw'>>,
): string {
  const srcWidth = source.naturalWidth || source.width;
  const srcHeight = source.naturalHeight || source.height;
  const width = Math.max(1, Math.round(srcWidth * upscaleFactor));
  const height = Math.max(1, Math.round(srcHeight * upscaleFactor));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source.src;

  // Flatten onto white, then upscale with high-quality interpolation.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);

  // Scale the unsharp radius with the upscale factor so the sharpening targets
  // facial-contour-scale edges (not just single pixels) after upscaling.
  const sharpenRadius = Math.max(1, Math.round(upscaleFactor));

  const imageData = ctx.getImageData(0, 0, width, height);
  applyColorControls(imageData, brightness, contrast, saturation);
  applySharpenLuminance(imageData, width, height, sharpness, sharpenRadius);
  ctx.putImageData(imageData, 0, 0);

  return canvas.toDataURL('image/png');
}

/**
 * Print badge PNG from the browser, replicating the kiosk image pipeline
 * (upscale + color controls + luminance sharpening) before handing the image to
 * the printer driver (which does the error-diffusion halftoning). Set the
 * Brother driver quality to high resolution for best results.
 *
 * @param badgeImage - Base64 data URI of the PNG image
 * @param options - Kiosk-style image processing options
 */
export async function printBadgePng(
  badgeImage: string,
  options: BadgePrintOptions = {},
): Promise<void> {
  const {
    upscaleFactor = DEFAULT_UPSCALE_FACTOR,
    brightness = DEFAULT_BRIGHTNESS,
    contrast = DEFAULT_CONTRAST,
    saturation = DEFAULT_SATURATION,
    sharpness = DEFAULT_SHARPNESS,
    raw = false,
  } = options;

  let printableImage = badgeImage;
  if (!raw) {
    try {
      const source = await loadImageElement(badgeImage);
      printableImage = processBadgeImage(source, {
        upscaleFactor,
        brightness,
        contrast,
        saturation,
        sharpness,
      });
    } catch {
      // Fall back to the raw PNG if processing fails.
      printableImage = badgeImage;
    }
  }

  // Create a hidden iframe for printing
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = '0';
  iframe.setAttribute('title', 'Badge Print');

  const cleanup = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  };

  iframe.onload = () => {
    const contentWindow = iframe.contentWindow;
    const contentDocument = iframe.contentDocument;

    if (!contentWindow || !contentDocument) {
      cleanup();
      return;
    }

    // Create HTML with the processed PNG. Smooth scaling (continuous-tone image;
    // the driver does the halftoning) and exact color output (no lightening).
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Visitor Badge</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          @page {
            size: ${BADGE_WIDTH_IN}in ${BADGE_HEIGHT_IN}in;
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            background: white;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          }
          img {
            display: block;
            width: ${BADGE_WIDTH_IN}in;
            height: ${BADGE_HEIGHT_IN}in;
            object-fit: contain;
            page-break-inside: avoid;
            image-rendering: auto;
            image-rendering: high-quality;
            color-adjust: exact;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          @media print {
            body {
              min-height: auto;
            }
          }
        </style>
      </head>
      <body>
        <img src="${printableImage}" alt="Visitor Badge" />
      </body>
      </html>
    `;

    contentDocument.open();
    contentDocument.write(html);
    contentDocument.close();

    // Wait for image to load before printing
    const img = contentDocument.querySelector('img');
    if (img) {
      img.onload = () => {
        const handleAfterPrint = () => {
          contentWindow.removeEventListener('afterprint', handleAfterPrint);
          cleanup();
        };

        contentWindow.addEventListener('afterprint', handleAfterPrint);
        contentWindow.focus();

        setTimeout(() => {
          contentWindow.print();
        }, 150);

        // Fallback cleanup in case afterprint doesn't fire
        setTimeout(cleanup, 30_000);
      };

      img.onerror = () => {
        cleanup();
      };
    } else {
      cleanup();
    }
  };

  document.body.appendChild(iframe);
}

/**
 * Print badge using local HTML template rendering (legacy)
 * @param badgeData - Badge data with display strings
 */
export async function printBadge(badgeData: BadgeData): Promise<void> {
  const normalizedVisitorType = (
    badgeData.visitorType || ''
  ).toLocaleUpperCase();

  const templateUrls = [
    '/visitor-badge.html',
    new URL('../public/visitor-badge.html', import.meta.url).toString(),
  ];

  const templateCandidates = await Promise.all(
    templateUrls.map(async (url) => {
      try {
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) return null;

        const html = await response.text();
        return html.includes('VISITOR FULL NAME') ? html : null;
      } catch {
        return null;
      }
    }),
  );

  const templateHtml = templateCandidates.find(
    (candidate): candidate is string => Boolean(candidate),
  );

  if (!templateHtml) {
    throw new Error('Failed to load badge template');
  }

  const renderedTemplateHtml = templateHtml
    .replaceAll('VISITOR TYPE', sanitizeText(normalizedVisitorType))
    .replaceAll('VISITOR FULL NAME', sanitizeText(badgeData.visitorName))
    .replaceAll('Visitor Company', sanitizeText(badgeData.visitorCompany))
    .replaceAll('HOST LABEL', sanitizeText(badgeData.hostLabel))
    .replaceAll('HOST NAME', sanitizeText(badgeData.hostName))
    .replaceAll('Org Name', sanitizeText(badgeData.orgName))
    .replaceAll('YYYY-MM-DD', sanitizeText(badgeData.visitDate));

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = '0';
  iframe.setAttribute('title', 'Badge Print');
  iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');

  const cleanup = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  };

  iframe.onload = () => {
    const contentWindow = iframe.contentWindow;
    if (!contentWindow) {
      cleanup();
      return;
    }

    const handleAfterPrint = () => {
      contentWindow.removeEventListener('afterprint', handleAfterPrint);
      cleanup();
    };

    contentWindow.addEventListener('afterprint', handleAfterPrint);
    contentWindow.focus();

    setTimeout(() => {
      contentWindow.print();
    }, 150);

    setTimeout(cleanup, 30_000);
  };

  document.body.appendChild(iframe);
  iframe.srcdoc = renderedTemplateHtml;
}

/**
 * Format visitor name (legacy)
 */
export function formatVisitorName(
  firstName: string | null,
  middleName: string | null,
  lastName: string | null,
): string {
  const capitalizeFirst = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';
    return trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
  };

  const parts = [firstName, middleName, lastName]
    .filter(Boolean)
    .map((part) => capitalizeFirst(part as string))
    .filter(Boolean);

  return parts.join(' ') || 'Unknown Visitor';
}

/**
 * Format visit date (legacy)
 */
export function formatVisitDate(dateString: string | null): string {
  if (!dateString) return new Date().toISOString().split('T')[0];
  try {
    return opTime(dateString).format('YYYY-MM-DD');
  } catch {
    return new Date().toISOString().split('T')[0];
  }
}

/**
 * Sanitize user-supplied text to prevent XSS attacks
 * Uses DOMPurify to strip all HTML tags, leaving only safe text content
 * @param text - User-supplied string that may contain HTML
 * @returns Sanitized text safe for insertion into HTML
 */
function sanitizeText(text: string): string {
  // Strip all HTML tags - only plain text is allowed in badge fields
  return DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true,
  });
}
